/* jshint esversion: 6 */
//
// Organizer-assisted "Resubscribe to emails" action for an Unsubscribed
// recipient in the Track → Email Tracking side panel.
//
// Flow:
//   1. preventDefault on the trigger ("..." menu link)
//   2. open a sweetAlert confirmation with a visible checkbox the organizer
//      MUST tick to acknowledge the person asked to receive emails again — the
//      confirm ("Restore Delivery") button stays disabled until it is checked
//   3. on confirm, POST the endpoint (list_item_id) as JSON
//   4. on success, reload the page so the Email Tracking graph and Unsubscribed
//      count update; the server sets a flash that renders after the reload
//
// Server contract (POST, JSON):
//   - param list_item_id: integer (the recipient's list_item in this org)
//   - 200: { status: "resubscribed" | "already_subscribed" } (+ flash message)
//   - 403: { error } when the user is not owner/admin/organizer
//   - 422: { error } when the recipient email can't be resolved
import { Controller } from 'stimulus';
import sweetAlert from 'sweetalert';
import I18n from '../i18n-js/index.js.erb';
import FlashMessage from '../application/app/flash_message';

const t = (key, opts) => I18n.t(key, {
  scope: 'js.my_events.email_resubscriptions',
  ...opts
});
const tShared = (key, opts) => I18n.t(key, {
  scope: 'shared',
  ...opts
});

export default class extends Controller {
  static values = {
    url: String,
    listItemId: Number
  };

  // Wired via data-action="click->email-resubscribe#open"
  open(event) {
    event.preventDefault();

    const content = document.createElement('div');
    const body = document.createElement('p');
    body.textContent = t('confirm.body');

    // A visible checkbox to the LEFT of the acknowledgement sentence. The whole
    // label is clickable, so clicking the text toggles the checkbox on/off too.
    const label = document.createElement('label');
    label.className = 'email-resubscribe__confirm';
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'email-resubscribe__checkbox';
    const acknowledgement = document.createElement('span');
    acknowledgement.className = 'email-resubscribe__ack';
    acknowledgement.textContent = t('confirm.checkbox');
    label.appendChild(checkbox);
    label.appendChild(acknowledgement);

    content.appendChild(body);
    content.appendChild(label);

    sweetAlert({
      title: t('confirm.title'),
      content,
      buttons: [tShared('buttons.cancel'), t('confirm.button')]
    }).then((confirmed) => {
      if (confirmed) {
        this._submit();
      }
    });

    // The confirm button is disabled until the acknowledgement is checked. No
    // existing dialog gates on a checkbox, so we toggle the sweetAlert confirm
    // button's disabled state directly (same idiom as table_flash_actions.js).
    const confirmButton = document.querySelector('.swal-button--confirm');
    if (confirmButton) {
      confirmButton.disabled = true;
      checkbox.addEventListener('change', () => {
        confirmButton.disabled = !checkbox.checked;
      });
    }
  }

  // --- private ---------------------------------------------------------

  async _submit() {
    try {
      await this._postJSON({ list_item_id: this.listItemIdValue });
    } catch (err) {
      FlashMessage.showMessageWithTimer('danger', err.message || t('errors.generic'));
      return;
    }

    // Reload so the Email Tracking graph and the Unsubscribed count update
    // immediately. The success message is set as a server flash and renders
    // after the reload.
    window.location.reload();
  }

  async _postJSON(body) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    const response = await fetch(this.urlValue, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken || ''
      },
      body: JSON.stringify(body)
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      const err = new Error(payload.error || t('errors.generic'));
      err.status = response.status;
      throw err;
    }
    return response.json();
  }
}
